import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../../../../../../navigators/ClubNavigator";
import { BookingWizardDataAtom } from "../../../../../atoms/stores";
import { useRepeatMultiSlotBooking } from "../hooks/useRepeatMultiSlotBooking";
import { useRepeatSingleSlotBooking } from "../hooks/useRepeatSingleSlotBooking";
import { PreviousBookingsListItem } from "./PreviousBookingsListItem";
import { PreviousBookingsListClient$key } from "./__generated__/PreviousBookingsListClient.graphql";

type PreviousBookingsListProps = {
  clientRef: PreviousBookingsListClient$key | null;
  onClose: () => void;
  allowedNumberOfPlayers: number;
};

export const PreviousBookingsList = ({
  clientRef,
  onClose,
  allowedNumberOfPlayers,
}: PreviousBookingsListProps) => {
  const { memberNonPlayingHostToggle } = useFlags();
  const wizardData = useAtomValue(BookingWizardDataAtom);
  const data = useFragment(PreviousBookingsListFragmentString, clientRef);
  const [repeatSingleSlotBooking, isRepeatingSingleSlot] =
    useRepeatSingleSlotBooking();

  const [repeatMultiSlotBooking, isRepeatingMultiSlot] =
    useRepeatMultiSlotBooking();

  const isRepeating = isRepeatingSingleSlot || isRepeatingMultiSlot;

  const navigation =
    useNavigation<
      NavigationProp<ClubStackNavigatorParamList, "BookingSummary">
    >();

  const onComplete = (bookingId: string) => {
    onClose();
    navigation.navigate("BookingSummary", {
      slotOrBookingId: bookingId,
      params: {},
    });
  };

  const handleBookingPress = (
    previousBookingId: string,
    durationMins?: number | null
  ) => {
    if (wizardData?.startSlotId) {
      if (durationMins && wizardData.type === "DurationBooking") {
        repeatMultiSlotBooking(
          {
            previousBookingId,
            startSlotId: wizardData?.startSlotId,
            durationMins: durationMins ?? 15,
          },
          onComplete
        );
        return;
      } else {
        repeatSingleSlotBooking(
          {
            previousBookingId,
            startSlotId: wizardData?.startSlotId,
          },
          onComplete
        );
      }
    }
  };

  const bookings = data?.repeatableBookings;
  const uniqueNameSets = new Set();

  const uniqueBookings = bookings?.filter((booking) => {
    const hasNonPlayingHost = booking?.rounds?.edges?.some(
      (edge) => edge?.node?.nonPlayingHost
    );

    if (!memberNonPlayingHostToggle && hasNonPlayingHost) {
      return false;
    }

    const uniquePlayerCount =
      booking?.rounds?.edges?.flatMap((edge) =>
        edge?.node?.player?.fullName ? edge.node.player.fullName : []
      ).length ?? 0;
    if (uniquePlayerCount <= 1) {
      return false;
    }

    const playerCount = booking?.rounds?.edges?.length ?? 0;

    const playerNameList =
      booking?.rounds?.edges
        ?.map((edge) => edge?.node?.player?.fullName ?? "Unknown Player")
        .sort() ?? [];

    const playerNames = playerNameList.join("|");

    if (uniqueNameSets.has(playerNames)) return false;
    uniqueNameSets.add(playerNames);

    const standardCapacity = booking?.startSlot?.standardCapacity;
    if (
      typeof standardCapacity === "number" &&
      playerCount > allowedNumberOfPlayers
    ) {
      return false;
    }
    return true;
  });

  const skeletonCards = Array.from({ length: 3 }, (_, index) => (
    <SkeletonLoader
      key={`previous-booking-skeleton-${index}`}
      height={88}
      borderRadius="2"
      width="100%"
    />
  ));

  return (
    <Box flex={1} bg="surface-secondary">
      {isRepeating ? (
        <VStack gap={4} px={5} pt={5} pb={6}>
          {skeletonCards}
        </VStack>
      ) : (
        uniqueBookings?.map((booking) => {
          return (
            <PreviousBookingsListItem
              key={booking?.id}
              bookingRef={booking ?? null}
              currentPatronId={data?.id ?? ""}
              onPress={handleBookingPress}
            />
          );
        })
      )}
    </Box>
  );
};

export const PreviousBookingsListFragmentString = graphql`
  fragment PreviousBookingsListClient on Client
  @argumentDefinitions(forSlotId: { type: "ID", defaultValue: null }) {
    ... on Member {
      id
      repeatableBookings(
        dateLte: $currentDate
        slotType: $slotType
        forSlotId: $forSlotId
      ) {
        ... on StandardBooking {
          id
          startSlot {
            type
            ... on TimeSlot {
              usedCapacity
              standardCapacity: capacity(bookingType: STANDARD)
            }
          }
          rounds {
            edges {
              node {
                id
                ... on MemberRound {
                  nonPlayingHost
                }
                player {
                  fullName
                  id
                }
              }
            }
          }
        }
        ...PreviousBookingsListItem
      }
    }
    ... on Customer {
      id
      repeatableBookings(
        dateLte: $currentDate
        slotType: $slotType
        forSlotId: $forSlotId
      ) {
        ... on StandardBooking {
          id
          startSlot {
            type
            ... on TimeSlot {
              usedCapacity
              standardCapacity: capacity(bookingType: STANDARD)
            }
          }
          rounds {
            edges {
              node {
                id
                ... on MemberRound {
                  nonPlayingHost
                }
                player {
                  fullName
                  id
                }
              }
            }
          }
        }
        ...PreviousBookingsListItem
      }
    }
  }
`;
